/**
 * TanStack Query Cache Configuration
 *
 * Tiered caching strategy:
 * - Metadata (projects, boards, config): 24h persist, long staleTime
 * - Active issues (To Do, In Progress): short staleTime, refetch on focus
 * - Done issues: long staleTime (they don't change)
 * - Comments: medium staleTime, invalidated on post
 */

import { QueryClient } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/query-persist-client-core';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

// ── Stale Time Constants ─────────────────────────────────────────────

export const STALE = {
  REALTIME: 1 * 60_000,       // 1 min — In Progress issues
  ACTIVE: 2 * 60_000,         // 2 min — To Do issues, issue detail
  MODERATE: 10 * 60_000,      // 10 min — backlog, search results
  COMMENTS: 5 * 60_000,       // 5 min — comments (invalidated on post)
  STABLE: 60 * 60_000,        // 1 hour — Done issues
  METADATA: 24 * 60 * 60_000, // 24 hours — projects, boards, config
  AUTH: 30 * 60_000,          // 30 min — auth/me
} as const;

// ── GC Time (how long to keep in cache after last observer unsubscribes)

const GC_TIME = 24 * 60 * 60_000; // 24 hours — keep everything in localStorage

// ── Query Client ─────────────────────────────────────────────────────

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE.ACTIVE,       // Default: 2 min
      gcTime: GC_TIME,
      retry: 1,
      refetchOnWindowFocus: true,    // Refetch stale data when user returns
      refetchOnReconnect: true,
    },
  },
});

// ── LocalStorage Persister ───────────────────────────────────────────

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'com.weblisters.jira-cache', // keep in sync with PERSIST_KEY
  // Only persist queries that are worth caching long-term
  // Skip large/volatile data to stay under localStorage ~5MB limit
  serialize: (data) => {
    // Filter out issue search results (volatile, can be large)
    // Keep: projects, boards, board config, auth, follows
    const filtered = {
      ...data,
      clientState: {
        ...data.clientState,
        queries: data.clientState.queries.filter((q) => {
          const key = q.queryKey[0] as string;
          // Persist metadata and board data
          // (auth is deliberately not persisted: identity must always come from the server)
          if (['projects', 'project', 'boards', 'board', 'follows', 'follow'].includes(key)) {
            return true;
          }
          // Persist individual issue details (small)
          if (key === 'issue') return true;
          // Skip: search results, comments (refetch is fast enough)
          return false;
        }),
      },
    };
    return JSON.stringify(filtered);
  },
  deserialize: (data) => JSON.parse(data),
});

const PERSIST_KEY = 'com.weblisters.jira-cache';

export function clearPersistedCache(): void {
  try {
    window.localStorage.removeItem(PERSIST_KEY);
  } catch {
    // storage unavailable (private mode) — nothing persisted anyway
  }
}

// ── Initialize Persistence ───────────────────────────────────────────

export function initQueryPersistence(): void {
  persistQueryClient({
    queryClient,
    persister,
    maxAge: GC_TIME,
    // Don't block rendering while restoring
    buster: 'v4-issue-links', // Bump when cached API shapes change (v4: links on issues/boards/follows)
  });
}
